import React, { FC, useEffect, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { PageLink, PageTitle } from '../../../../rbts/layout/core';
import { KTSVG, SimpleAlert } from '../../../../rbts/helpers';
import { RootState } from '../../../../setup';
import {
  getProductosFRService,
  getPuntosVentaFRService,
  getUltimoEventoFRService,
  procesarPagoFRService,
  Producto,
  ProductoCarrito,
  PuntoVentaFR,
  RostroCliente,
  FaceEvent,
} from '../../../service/face_recognition/FaceRecognitionService';
import { backendFileUrl } from '../backendUrl';
import { obtenerTicket, conTicket } from '../streamTicket';

const API_URL = process.env.REACT_APP_API;
const breadCrumbs: Array<PageLink> = [];

const PagoFaceRecognitionPage: FC = () => {
  const token = useSelector<RootState>(({ auth }) => (auth as any).accessToken, shallowEqual) as string;

  /*
   * El puesto manda: define qué productos se pueden vender, a qué precio, y a
   * qué día operativo se atribuye la venta. Por eso se elige ANTES que nada y
   * al cambiarlo se vacía el carrito: los precios del puesto anterior ya no
   * aplican.
   */
  const [puntosVenta, setPuntosVenta] = useState<PuntoVentaFR[]>([]);
  const [puntoVentaId, setPuntoVentaId] = useState<string>('');
  const [cargandoPuestos, setCargandoPuestos] = useState(true);

  const [productos, setProductos] = useState<Producto[]>([]);
  const [cargandoProductos, setCargandoProductos] = useState(false);
  const [carrito, setCarrito] = useState<ProductoCarrito[]>([]);
  const [selectedProductoId, setSelectedProductoId] = useState<string>('');
  const [cantidad, setCantidad] = useState<number>(1);

  const [rostroReconocido, setRostroReconocido] = useState<RostroCliente | null>(null);
  const [snapshotUrl, setSnapshotUrl] = useState<string>('');
  const [scanning, setScanning] = useState(false);
  const [paying, setPaying] = useState(false);
  const [pagoExitoso, setPagoExitoso] = useState<{ monto: number; saldo: number; puesto?: string } | null>(null);
  const [sseConectado, setSseConectado] = useState(false);
  // El correo se muestra enmascarado: esta pantalla está a la vista del público
  // en la caja, y no hay razón para exponer el correo completo de un cliente.
  const [mostrarCorreo, setMostrarCorreo] = useState(false);

  /*
   * El cobro no se dispara con el botón Pagar: abre una confirmación con foto,
   * nombre y total. El carrito sobrevive entre escaneos (el cajero captura la
   * mercancía antes de que el cliente llegue), así que sin este paso bastaría
   * escanear a la persona equivocada para cobrarle los productos de otra.
   */
  const [confirmando, setConfirmando] = useState(false);

  /*
   * Un reconocimiento no vale para siempre: si el cajero escanea y luego tarda
   * capturando, el cliente ya se fue de la terminal y el cobro seguiría
   * apuntando a él. A los 2 minutos se descarta y hay que volver a escanear.
   */
  const [segundosVigencia, setSegundosVigencia] = useState<number | null>(null);

  /*
   * Clave de idempotencia del intento de cobro en curso. Se genera al pulsar
   * Pagar y se CONSERVA si el intento falla, para que un reintento del cajero
   * no genere un segundo cargo. Se limpia al concluir el cobro o al cambiar de
   * cliente.
   */
  const idempotencyRef = useRef<string | null>(null);

  /**
   * Genera la clave de idempotencia del intento de cobro.
   *
   * Se usa `crypto.getRandomValues` (128 bits) y no un timestamp con
   * Math.random: si dos cajas generaran la MISMA clave, el backend tomaría el
   * segundo cobro por un reintento del primero y lo devolvería como repetida —
   * es decir, entregaría mercancía sin cobrarla, en silencio. Con 128 bits esa
   * colisión es inalcanzable en la práctica.
   *
   * Tampoco se usa crypto.randomUUID porque no está en las typings de DOM de
   * este proyecto.
   */
  function nuevaClaveIdempotencia(): string {
    const bytes = new Uint8Array(16);
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    const hex = Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    return `fr-${Date.now().toString(36)}-${hex}`;
  }

  const eventSourceRef = useRef<EventSource | null>(null);
  const snapshotTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /**
   * El handler del SSE se registra una sola vez (useEffect con []), así que
   * capturaría `scanning` en su valor inicial (false) para siempre. Se espeja el
   * estado en un ref para poder consultarlo con el valor vigente desde el handler.
   */
  const ticketRef = useRef('');
  const reconexionRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scanningRef = useRef(false);
  function actualizarScanning(v: boolean) {
    scanningRef.current = v;
    setScanning(v);
  }

  // Mismo motivo que scanningRef: el temporizador de vigencia lee este valor
  // desde un intervalo que se registró una sola vez.
  const payingRef = useRef(false);
  function actualizarPaying(v: boolean) {
    payingRef.current = v;
    setPaying(v);
  }

  /** Vigencia del reconocimiento, en milisegundos. */
  const VIGENCIA_RECONOCIMIENTO_MS = 120000;
  const expiraEnRef = useRef(0);
  const vigenciaTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function detenerVigencia() {
    if (vigenciaTimerRef.current) { clearInterval(vigenciaTimerRef.current); vigenciaTimerRef.current = null; }
    setSegundosVigencia(null);
  }

  function iniciarVigencia() {
    expiraEnRef.current = Date.now() + VIGENCIA_RECONOCIMIENTO_MS;
    setSegundosVigencia(Math.ceil(VIGENCIA_RECONOCIMIENTO_MS / 1000));
    if (vigenciaTimerRef.current) clearInterval(vigenciaTimerRef.current);
    vigenciaTimerRef.current = setInterval(() => {
      // Con un cobro en vuelo no se descarta al cliente: la respuesta del
      // servidor ya viene en camino y quitarlo dejaría la pantalla sin saber
      // a quién se le cobró.
      if (payingRef.current) return;
      const restante = Math.ceil((expiraEnRef.current - Date.now()) / 1000);
      if (restante <= 0) {
        detenerVigencia();
        setRostroReconocido(null);
        setConfirmando(false);
        idempotencyRef.current = null;
        SimpleAlert('El reconocimiento caducó. Vuelva a escanear al cliente.', 3000, 'warning');
        return;
      }
      setSegundosVigencia(restante);
    }, 1000);
  }

  useEffect(() => {
    loadPuntosVenta();

    // El ticket dura 5 min; se renueva cada 4 para que nunca caduque en uso.
    let renovacion: ReturnType<typeof setInterval>;
    (async () => {
      await renovarTicket();
      connectSse();
      renovacion = setInterval(() => { renovarTicket().catch(() => { }); }, 4 * 60 * 1000);
    })();

    return () => {
      eventSourceRef.current?.close();
      if (snapshotTimerRef.current) clearInterval(snapshotTimerRef.current);
      if (reconexionRef.current) clearTimeout(reconexionRef.current);
      if (vigenciaTimerRef.current) clearInterval(vigenciaTimerRef.current);
      if (renovacion) clearInterval(renovacion);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cambiar de puesto recarga su catálogo y vacía el carrito armado con el anterior.
  useEffect(() => {
    setCarrito([]);
    setSelectedProductoId('');
    setCantidad(1);
    if (!puntoVentaId) {
      setProductos([]);
      return;
    }
    loadProductos(parseInt(puntoVentaId, 10));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puntoVentaId]);

  async function renovarTicket(forzar = false) {
    try {
      ticketRef.current = await obtenerTicket(token, forzar);
    } catch (err) {
      console.warn('[SSE] no se pudo obtener el ticket de acceso:', err);
      setSseConectado(false);
    }
  }

  async function loadPuntosVenta() {
    setCargandoPuestos(true);
    try {
      const data = await getPuntosVentaFRService(token);
      // Solo se muestran los puntos de venta de tipo PUESTO
      const puestos = data.filter((pv) => pv.tipo === 'PUESTO');
      setPuntosVenta(puestos);
      // Con un solo puesto disponible no tiene sentido obligar a elegirlo.
      if (puestos.length === 1) setPuntoVentaId(String(puestos[0].id));
    } catch {
      SimpleAlert('Error al cargar los puntos de venta.', 2000, 'error');
    } finally {
      setCargandoPuestos(false);
    }
  }

  async function loadProductos(pvId: number) {
    setCargandoProductos(true);
    try {
      const data = await getProductosFRService(token, pvId);
      setProductos(data);
    } catch {
      SimpleAlert('Error al cargar productos.', 2000, 'error');
      setProductos([]);
    } finally {
      setCargandoProductos(false);
    }
  }

  function connectSse() {
    if (eventSourceRef.current) eventSourceRef.current.close();

    const url = conTicket(`${API_URL}faceRecognition/stream`, ticketRef.current);
    console.log('[SSE] conectando a', url);
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onopen = () => {
      console.log('[SSE] conectado');
      setSseConectado(true);
    };

    es.addEventListener('face:recognized', ((e: MessageEvent) => {
      console.log('[SSE] face:recognized recibido — scanning =', scanningRef.current, e.data);
      // Sin escaneo activo se ignora: el terminal reconoce a cualquiera que pase
      // enfrente, y eso cambiaría el cliente del cobro en curso.
      if (!scanningRef.current) return;
      try {
        aplicarEvento(JSON.parse(e.data) as FaceEvent);
      } catch (err) {
        console.warn('[SSE] no se pudo procesar el evento:', err);
      }
    }) as EventListener);

    es.addEventListener('face:unknown', (() => {
      if (!scanningRef.current) return;
      SimpleAlert('Rostro detectado pero no registrado en el sistema.', 3000, 'warning');
      actualizarScanning(false);
      stopSnapshotPolling();
    }) as EventListener);

    es.onerror = () => {
      // EventSource reintenta solo, pero un fallo permanente sería invisible:
      // el cajero se quedaría frente a una pantalla que nunca reacciona.
      setSseConectado(false);
      console.warn('[SSE] error de conexión — readyState =', es.readyState,
        es.readyState === 2 ? '(CERRADO, no reintentará)' : '(reintentando...)');

      /*
       * El reintento nativo reusaría la MISMA URL, con el ticket ya vencido, y
       * quedaría en un bucle de 401. Se cierra, se pide ticket nuevo y se vuelve
       * a conectar con una URL válida.
       */
      es.close();
      if (reconexionRef.current) clearTimeout(reconexionRef.current);
      reconexionRef.current = setTimeout(async () => {
        await renovarTicket(true);
        connectSse();
      }, 3000);
    };
  }

  /** Aplica un evento de reconocimiento a la pantalla. */
  function aplicarEvento(data: FaceEvent) {
    setMostrarCorreo(false);   // cada cliente empieza con el correo oculto
    idempotencyRef.current = null;   // cliente nuevo, intento nuevo
    if (data.rostroCliente) {
      setRostroReconocido(data.rostroCliente);
      iniciarVigencia();
    } else {
      setRostroReconocido(null);
      detenerVigencia();
      SimpleAlert('Rostro no registrado en el sistema.', 3000, 'warning');
    }
    actualizarScanning(false);
    stopSnapshotPolling();
  }

  function startSnapshotPolling() {
    if (snapshotTimerRef.current) clearInterval(snapshotTimerRef.current);
    refreshSnapshot();
    snapshotTimerRef.current = setInterval(refreshSnapshot, 1000);
  }

  function stopSnapshotPolling() {
    if (snapshotTimerRef.current) { clearInterval(snapshotTimerRef.current); snapshotTimerRef.current = null; }
    // Sin esto la vista se queda congelada en el último snapshot y la rama que
    // muestra la foto enrolada del cliente nunca se alcanza.
    setSnapshotUrl('');
  }

  async function refreshSnapshot() {
    try {
      const ticket = await obtenerTicket(token);
      ticketRef.current = ticket;
      setSnapshotUrl(conTicket(`${API_URL}faceRecognition/snapshot?t=${Date.now()}`, ticket));
    } catch {
      // si no se puede renovar, se conserva la URL anterior sin romper el polling
    }
  }

  /** Ventana en la que se acepta un reconocimiento ocurrido ANTES de pulsar Escanear. */
  const VENTANA_EVENTO_PREVIO_MS = 10000;

  async function handleEscanear() {
    setRostroReconocido(null);
    detenerVigencia();
    setConfirmando(false);
    setPagoExitoso(null);
    actualizarScanning(true);
    startSnapshotPolling();

    /*
     * El cliente suele estar ya frente al terminal cuando el cajero pulsa
     * Escanear, así que su reconocimiento ocurrió segundos antes y el gating lo
     * descartó. Además el backend aplica un cooldown por persona, de modo que el
     * siguiente evento podría tardar. Se rescata el último reconocimiento
     * reciente en vez de dejar al cajero esperando uno que quizá no vuelva.
     */
    try {
      const ultimo = await getUltimoEventoFRService(token);
      const capturado = ultimo?.capturedAt ? new Date(ultimo.capturedAt).getTime() : 0;
      if (ultimo?.rostroCliente && Date.now() - capturado < VENTANA_EVENTO_PREVIO_MS) {
        console.log('[SSE] recuperado reconocimiento previo de', ultimo.rostroCliente.nombre);
        aplicarEvento(ultimo);
        return;
      }
    } catch (err) {
      console.warn('[SSE] no se pudo consultar el último evento:', err);
    }

    SimpleAlert('Acerque el rostro al dispositivo...', 3000, 'info');
  }

  function handleCancelar() {
    idempotencyRef.current = null;
    setRostroReconocido(null);
    setConfirmando(false);
    detenerVigencia();
    actualizarScanning(false);
    setPagoExitoso(null);
    stopSnapshotPolling();
    setSnapshotUrl('');
  }

  function agregarProducto() {
    if (!selectedProductoId) return;
    const prod = productos.find((p) => p.id === parseInt(selectedProductoId));
    if (!prod) return;
    const cant = Math.trunc(Number(cantidad));
    if (!(cant > 0)) {
      SimpleAlert('La cantidad debe ser mayor a cero.', 2000, 'error');
      return;
    }

    // Agregar dos veces el mismo producto suma cantidades en vez de duplicar el
    // renglón: el ticket queda legible y el corte también.
    setCarrito((prev) => {
      const i = prev.findIndex((item) => item.id === prod.id);
      if (i >= 0) {
        const copia = [...prev];
        copia[i] = { ...copia[i], cantidad: copia[i].cantidad + cant };
        return copia;
      }
      return [...prev, { ...prod, precio: parseFloat(String(prod.costo_sugerido)) || 0, cantidad: cant }];
    });
    setSelectedProductoId('');
    setCantidad(1);
  }

  function cambiarCantidad(index: number, valor: number) {
    const cant = Math.trunc(Number(valor));
    if (!(cant > 0)) return;
    setCarrito((prev) => prev.map((item, i) => (i === index ? { ...item, cantidad: cant } : item)));
  }

  function quitarProducto(index: number) {
    setCarrito((prev) => prev.filter((_, i) => i !== index));
  }

  const mmss = (segs: number) => `${Math.floor(segs / 60)}:${String(segs % 60).padStart(2, '0')}`;

  const totalCarrito = carrito.reduce((sum, p) => sum + p.precio * p.cantidad, 0);
  const puestoSeleccionado = puntosVenta.find((pv) => String(pv.id) === puntoVentaId);

  async function handlePagar() {
    if (!puntoVentaId) { SimpleAlert('Seleccione el punto de venta.', 2000, 'error'); return; }
    if (!rostroReconocido) { SimpleAlert('Debe escanear un rostro válido.', 2000, 'error'); return; }
    if (carrito.length === 0) { SimpleAlert('Agregue al menos un producto.', 2000, 'error'); return; }
    if (!rostroReconocido.tiene_wallet) {
      SimpleAlert('Este rostro no tiene monedero de Wallet vinculado. Enlázalo desde el catálogo.', 4000, 'error');
      return;
    }
    if (parseFloat(String(rostroReconocido.saldo)) < totalCarrito) {
      SimpleAlert(`Saldo insuficiente. Disponible: $${parseFloat(String(rostroReconocido.saldo)).toFixed(2)}`, 3000, 'error');
      return;
    }

    setConfirmando(true);
  }

  /** Cobro real, ya confirmado por el cajero contra la foto y el nombre. */
  async function confirmarCobro() {
    if (!rostroReconocido) return;
    actualizarPaying(true);
    try {
      if (!idempotencyRef.current) idempotencyRef.current = nuevaClaveIdempotencia();
      const result = await procesarPagoFRService(
        rostroReconocido.id,
        parseInt(puntoVentaId, 10),
        carrito,
        token,
        idempotencyRef.current
      );
      idempotencyRef.current = null;   // cobro concluido: el siguiente usa clave nueva
      setPagoExitoso({
        monto: result.monto_total,
        saldo: result.saldo_restante,
        puesto: result.punto_venta?.nombre ?? puestoSeleccionado?.nombre,
      });
      setCarrito([]);
      setRostroReconocido(null);
      setConfirmando(false);
      detenerVigencia();
      setSnapshotUrl('');
    } catch (err: any) {
      SimpleAlert(err?.response?.data?.message || 'Error al procesar el pago.', 3000, 'error');
    } finally {
      actualizarPaying(false);
    }
  }

  const tieneWallet = !!rostroReconocido?.tiene_wallet;
  const saldoSuficiente =
    rostroReconocido && tieneWallet && parseFloat(String(rostroReconocido.saldo)) >= totalCarrito;

  return (
    <>
      <PageTitle breadcrumbs={breadCrumbs}>Pago Face Recognition</PageTitle>

      {pagoExitoso && (
        <div className="alert alert-success d-flex align-items-center mb-6">
          <KTSVG path="/media/icons/duotune/general/gen043.svg" className="svg-icon-2hx svg-icon-success me-4" />
          <div>
            <h5 className="mb-1">¡Pago procesado exitosamente!</h5>
            <span>
              Total cobrado: <strong>${pagoExitoso.monto.toFixed(2)}</strong> — Saldo restante del cliente: <strong>${pagoExitoso.saldo.toFixed(2)}</strong>
              {pagoExitoso.puesto ? <> — Puesto: <strong>{pagoExitoso.puesto}</strong></> : null}
            </span>
          </div>
        </div>
      )}

      <div className="row g-6">
        {/* ── Parte 1 + 3: Cámara y botones ── */}
        <div className="col-lg-5">
          <div className="card h-100">
            <div className="card-header border-0 pt-5">
              <h3 className="card-title fw-bold fs-5">Cámara en Vivo</h3>
            </div>
            <div className="card-body d-flex flex-column align-items-center justify-content-center pb-6">
              {/* Parte 1: Vista de cámara */}
              <div
                className="rounded overflow-hidden bg-dark d-flex align-items-center justify-content-center mb-4"
                style={{ width: '100%', maxWidth: 360, height: 280, position: 'relative' }}
              >
                {snapshotUrl ? (
                  <img
                    src={snapshotUrl}
                    alt="snapshot"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={() => setSnapshotUrl('')}
                  />
                ) : rostroReconocido?.foto_url ? (
                  <img
                    src={backendFileUrl(rostroReconocido.foto_url)}
                    alt={rostroReconocido.nombre}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <div className="text-center text-muted">
                    <KTSVG path="/media/icons/duotune/communication/com006.svg" className="svg-icon-5x opacity-25" />
                    <div className="mt-2 fs-7">Sin señal de cámara</div>
                  </div>
                )}
                {scanning && (
                  <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ background: 'rgba(0,0,0,0.4)' }}
                  >
                    <div className="text-center text-white">
                      <span className="spinner-border text-warning mb-2" />
                      <div className="fs-7">Buscando rostro...</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Parte 3: Botones Escanear / Cancelar */}
              <div className="d-flex gap-3">
                <button
                  className="btn btn-warning fw-bold"
                  onClick={handleEscanear}
                  disabled={scanning || paying}
                >
                  <KTSVG path="/media/icons/duotune/general/gen004.svg" className="svg-icon-3 me-2" />
                  Escanear
                </button>
                <button
                  className="btn btn-warning fw-bold"
                  onClick={handleCancelar}
                  disabled={paying}
                >
                  Cancelar
                </button>
              </div>

              {!sseConectado && (
                <div className="mt-3 text-warning fs-8 d-flex align-items-center gap-2">
                  <span className="spinner-border spinner-border-sm" />
                  Sin conexión con el servidor de eventos — el reconocimiento no llegará
                </div>
              )}

              {/* Info del cliente reconocido */}
              {rostroReconocido && (
                <div className={`mt-4 p-4 rounded w-100 ${saldoSuficiente ? 'bg-light-success' : 'bg-light-danger'}`}>
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div className="fw-bold fs-5">{rostroReconocido.nombre}</div>
                    {segundosVigencia !== null && (
                      <span
                        className={`badge ${segundosVigencia <= 30 ? 'badge-light-danger' : 'badge-light-primary'} fs-8`}
                        title="Tiempo que queda de validez del reconocimiento"
                      >
                        Vigencia {mmss(segundosVigencia)}
                      </span>
                    )}
                  </div>
                  <div className="text-muted fs-7">{rostroReconocido.telefono || 'Sin teléfono'}</div>

                  {rostroReconocido.tiene_wallet ? (
                    <div className="d-flex align-items-center gap-2 mt-1">
                      <span className="text-muted fs-7">
                        {mostrarCorreo
                          ? rostroReconocido.wallet_email
                          : rostroReconocido.wallet_email_mascarado}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0 fs-8"
                        onClick={() => setMostrarCorreo((v) => !v)}
                      >
                        {mostrarCorreo ? 'ocultar' : 'ver'}
                      </button>
                    </div>
                  ) : (
                    <div className="text-danger fs-7 mt-1 fw-bold">
                      Sin monedero de Wallet vinculado — no puede comprar
                    </div>
                  )}

                  <div className="mt-2">
                    Saldo: <span className={`fw-bold fs-5 ${saldoSuficiente ? 'text-success' : 'text-danger'}`}>
                      ${parseFloat(String(rostroReconocido.saldo)).toFixed(2)}
                    </span>
                    {!saldoSuficiente && carrito.length > 0 && (
                      <span className="text-danger ms-2 fs-7">— Saldo insuficiente</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Parte 2 + 4: Productos y Pago ── */}
        <div className="col-lg-7">
          <div className="card h-100">
            <div className="card-header border-0 pt-5">
              <h3 className="card-title fw-bold fs-5">Productos</h3>
              {puestoSeleccionado && (
                <div className="card-toolbar">
                  <span className="badge badge-light-primary fs-8">
                    Día operativo #{puestoSeleccionado.id_dia}
                  </span>
                </div>
              )}
            </div>
            <div className="card-body pb-4">
              {/* Punto de venta: define catálogo, precios y día operativo */}
              <div className="mb-4">
                <label className="form-label fs-7 fw-bold">Punto de venta</label>
                <select
                  className="form-select"
                  value={puntoVentaId}
                  onChange={(e) => setPuntoVentaId(e.target.value)}
                  disabled={cargandoPuestos || paying}
                >
                  <option value="">
                    {cargandoPuestos ? 'Cargando puestos...' : 'Seleccione un punto de venta'}
                  </option>
                  {puntosVenta.map((pv) => (
                    <option key={pv.id} value={pv.id}>
                      {pv.nombre}
                    </option>
                  ))}
                </select>
                {!cargandoPuestos && puntosVenta.length === 0 && (
                  <div className="text-danger fs-8 mt-2">
                    No hay puestos con día operativo abierto. Tesorería debe iniciar el día para poder cobrar.
                  </div>
                )}
              </div>

              {/* Parte 2: Selector de productos del puesto */}
              <div className="d-flex gap-3 mb-4 align-items-end">
                <div className="flex-grow-1">
                  <label className="form-label fs-7 fw-bold">Producto</label>
                  <select
                    className="form-select"
                    value={selectedProductoId}
                    onChange={(e) => setSelectedProductoId(e.target.value)}
                    disabled={!puntoVentaId || cargandoProductos || paying}
                  >
                    <option value="">
                      {!puntoVentaId
                        ? 'Primero seleccione el punto de venta'
                        : cargandoProductos
                          ? 'Cargando productos...'
                          : productos.length === 0
                            ? 'Este puesto no tiene productos asignados'
                            : 'Seleccione un producto'}
                    </option>
                    {productos.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.nombre} — ${parseFloat(String(p.costo_sugerido)).toFixed(2)}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ width: 110 }}>
                  <label className="form-label fs-7 fw-bold">Cantidad</label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    className="form-control"
                    value={cantidad}
                    onChange={(e) => setCantidad(parseInt(e.target.value || '1', 10))}
                    disabled={!puntoVentaId || paying}
                  />
                </div>
                <button
                  className="btn btn-primary"
                  onClick={agregarProducto}
                  disabled={!selectedProductoId || paying}
                >
                  Agregar
                </button>
              </div>

              {/* Lista del carrito */}
              {carrito.length === 0 ? (
                <div className="text-muted text-center py-6 fs-7">Selecciona productos para agregar</div>
              ) : (
                <div className="table-responsive mb-4">
                  <table className="table table-row-bordered align-middle gs-0 gy-2">
                    <thead>
                      <tr className="fw-bold text-muted fs-7">
                        <th>#</th>
                        <th>Producto</th>
                        <th className="text-center" style={{ width: 110 }}>Cantidad</th>
                        <th className="text-end">P. unitario</th>
                        <th className="text-end">Importe</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {carrito.map((item, i) => (
                        <tr key={item.id}>
                          <td className="text-muted">{i + 1}</td>
                          <td className="fw-bold">{item.nombre}</td>
                          <td className="text-center">
                            <input
                              type="number"
                              min={1}
                              step={1}
                              className="form-control form-control-sm text-center"
                              value={item.cantidad}
                              onChange={(e) => cambiarCantidad(i, parseInt(e.target.value || '1', 10))}
                              disabled={paying}
                            />
                          </td>
                          <td className="text-end">${item.precio.toFixed(2)}</td>
                          <td className="text-end fw-bold">${(item.precio * item.cantidad).toFixed(2)}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-icon btn-light-danger"
                              onClick={() => quitarProducto(i)}
                              disabled={paying}
                            >
                              <KTSVG path="/media/icons/duotune/general/gen027.svg" className="svg-icon-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={4} className="fw-bold text-end fs-5">Total:</td>
                        <td className="fw-bold text-end fs-5">${totalCarrito.toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* Parte 4: Botón Pagar */}
            <div className="card-footer d-flex justify-content-end py-4">
              <button
                className="btn btn-warning fw-bold px-8"
                onClick={handlePagar}
                disabled={!puntoVentaId || !rostroReconocido || carrito.length === 0 || !saldoSuficiente || paying}
              >
                {paying ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                Pagar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/*
        Confirmación explícita antes de mover dinero. Se muestra la foto y el
        nombre junto al total para que el cajero valide que el rostro que quedó
        en pantalla es el de la persona que tiene enfrente.
      */}
      {confirmando && rostroReconocido && (
        <>
          <div className="modal-backdrop fade show" />
          <div className="modal fade show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header py-4">
                  <h5 className="modal-title">Confirmar cobro</h5>
                  {segundosVigencia !== null && (
                    <span className={`badge ${segundosVigencia <= 30 ? 'badge-light-danger' : 'badge-light-primary'} fs-8`}>
                      Vigencia {mmss(segundosVigencia)}
                    </span>
                  )}
                </div>

                <div className="modal-body">
                  <div className="d-flex align-items-center gap-4 mb-4">
                    <div
                      className="rounded overflow-hidden bg-dark flex-shrink-0"
                      style={{ width: 84, height: 84 }}
                    >
                      {rostroReconocido.foto_url ? (
                        <img
                          src={backendFileUrl(rostroReconocido.foto_url)}
                          alt={rostroReconocido.nombre}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : null}
                    </div>
                    <div>
                      <div className="fw-bold fs-4">{rostroReconocido.nombre}</div>
                      <div className="text-muted fs-7">{rostroReconocido.wallet_email_mascarado}</div>
                      <div className="fs-7 mt-1">
                        Saldo actual:{' '}
                        <strong>${parseFloat(String(rostroReconocido.saldo)).toFixed(2)}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="separator mb-4" />

                  <div className="fs-7 text-muted mb-2">
                    Puesto: <strong className="text-gray-800">{puestoSeleccionado?.nombre}</strong>
                  </div>

                  <table className="table table-row-dashed align-middle gs-0 gy-2 mb-4">
                    <tbody>
                      {carrito.map((item) => (
                        <tr key={item.id}>
                          <td className="fw-bold">{item.nombre}</td>
                          <td className="text-muted text-center">x{item.cantidad}</td>
                          <td className="text-end">${(item.precio * item.cantidad).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold fs-4">Total a cobrar</span>
                    <span className="fw-bold fs-2 text-warning">${totalCarrito.toFixed(2)}</span>
                  </div>
                  <div className="text-muted fs-8 text-end mt-1">
                    Saldo tras el cobro: $
                    {(parseFloat(String(rostroReconocido.saldo)) - totalCarrito).toFixed(2)}
                  </div>
                </div>

                <div className="modal-footer py-4">
                  <button
                    className="btn btn-light"
                    onClick={() => setConfirmando(false)}
                    disabled={paying}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn btn-warning fw-bold px-8"
                    onClick={confirmarCobro}
                    disabled={paying}
                  >
                    {paying ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                    Confirmar cobro
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export { PagoFaceRecognitionPage };
